export function convertToFileName(text: string) {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\w\s]/gi, '')
    .replace(/\s+/g, '-');
}

export function getImageExtension(base64Image: string) {
  const match = base64Image.match(/^data:image\/(\w+);base64,/);

  if (!match?.[1]) {
    throw new Error('Base64 image is invalid or not supported');
  }

  return match[1];
}

export function dataURItoBlob(dataURI: string) {
  const [header = '', payload = ''] = dataURI.split(',');
  const byteString = header.includes('base64')
    ? atob(payload)
    : unescape(payload);
  const mimeString = header.split(':')[1]?.split(';')[0] ?? '';

  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i += 1) {
    bytes[i] = byteString.charCodeAt(i);
  }

  return new Blob([bytes], { type: mimeString });
}

/**
 * The media types the API accepts, by the extension they are stored under. Only
 * consulted for a file the browser gave no type for — it has no mapping for the
 * extension, which is the usual story for the .heic an iPhone shoots.
 */
const MEDIA_TYPE_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
  heif: 'image/heif',
};

/**
 * What the file claims to be, falling back to its extension whenever the browser did
 * not name an image type. An empty type is the familiar case — no mapping for the
 * extension — but some Android pickers go further and report `application/octet-stream`
 * for the very same `.heic`, and taking that at its word turned a perfectly good photo
 * into a file we refused to send. A guess costs little: the API sniffs the bytes and
 * refuses a file whose content disagrees with the type declared for it, so a wrong
 * answer here is caught rather than trusted.
 */
function mediaTypeOf(file: File) {
  if (file.type.startsWith('image/')) {
    return file.type;
  }

  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

  return MEDIA_TYPE_BY_EXTENSION[extension];
}

/**
 * Reads an image file into a data URL byte for byte, with no decode and re-encode in
 * between, so what travels is exactly what the camera produced.
 */
export function readImageFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const mediaType = mediaTypeOf(file);

    if (!mediaType?.startsWith('image/')) {
      reject(new Error('File Is NOT Image!'));
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const dataUrl = reader.result?.toString();

      if (!dataUrl) {
        reject(new Error('File Could Not Be Read!'));
        return;
      }

      // A file the browser gave no type for is read back as application/octet-stream,
      // which the API turns away on the header alone — before it ever looks at the
      // bytes, which are a perfectly good photo. The payload is untouched; only the
      // type the data URL declares is corrected.
      resolve(dataUrl.replace(/^data:[^;,]*/, `data:${mediaType}`));
    };
    reader.onerror = () => reject(new Error('File Could Not Be Read!'));

    reader.readAsDataURL(file);
  });
}
